import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import type { AppointmentRow } from '@/hooks/useAppointments';
import { theme } from '@/lib/theme';

type PaymentMethod = 'cash' | 'transfer';

interface Props {
  appointment: AppointmentRow | null;
  completing: boolean;
  onConfirm: (method: PaymentMethod) => Promise<void>;
  onClose: () => void;
}

interface PaymentSummary {
  servicePrice: number;
  advanceAmount: number;
  remainingAmount: number;
  loading: boolean;
  error: string | null;
}

function formatCOP(amount: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function CompleteServiceModal({ appointment, completing, onConfirm, onClose }: Props) {
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [summary, setSummary] = useState<PaymentSummary>({
    servicePrice: 0,
    advanceAmount: 0,
    remainingAmount: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!appointment) return;

    let cancelled = false;

    async function fetchSummary() {
      setSummary((prev) => ({ ...prev, loading: true, error: null }));

      try {
        // Fetch service price
        const { data: apptData, error: apptErr } = await supabase
          .from('appointments')
          .select('service_id, services(price)')
          .eq('id', appointment!.id)
          .maybeSingle();

        if (apptErr) throw apptErr;
        const servicePrice = (apptData?.services as { price: number } | null)?.price ?? 0;

        // Fetch advance payment — aceptar paid, pending (mock sin expirar) y failed con amount=0
        const { data: payments, error: payErr } = await supabase
          .from('payments')
          .select('amount, status')
          .eq('appointment_id', appointment!.id)
          .eq('type', 'advance')
          .in('status', ['paid', 'pending', 'failed']);

        if (payErr) throw payErr;
        // Supabase devuelve amount como string ("0.00"), usar Number() al comparar
        const paidAdv = payments?.find((p) => p.status === 'paid');
        const pendingAdv = payments?.find((p) => p.status === 'pending');
        const mockAdv = payments?.find((p) => p.status === 'failed' && Number(p.amount) === 0);
        // Si no hay anticipo (confirmada manualmente antes del fix) → tratar como $0
        const advanceAmount = Number((paidAdv ?? pendingAdv ?? mockAdv)?.amount ?? 0);
        const remainingAmount = Math.max(0, servicePrice - advanceAmount);

        if (!cancelled) {
          setSummary({ servicePrice, advanceAmount, remainingAmount, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Error al calcular el pago';
          setSummary((prev) => ({ ...prev, loading: false, error: msg }));
        }
      }
    }

    fetchSummary();
    return () => { cancelled = true; };
  }, [appointment]);

  if (!appointment) return null;

  const handleConfirm = async () => {
    await onConfirm(method);
  };

  return (
    <AnimatePresence>
      {appointment && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm"
            onClick={!completing ? onClose : undefined}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="w-full max-w-sm rounded-2xl border pointer-events-auto"
              style={{
                backgroundColor: theme.colors.bgSurface,
                borderColor: theme.colors.border,
              }}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-6 py-5 border-b"
                style={{ borderColor: theme.colors.border }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-xl"
                    style={{ backgroundColor: 'rgba(16,185,129,0.12)' }}
                  >
                    <i className="ri-check-double-line text-base text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-white text-base font-semibold">Completar servicio</h2>
                    <p className="text-white/40 text-xs mt-0.5">{appointment.client_name}</p>
                  </div>
                </div>
                {!completing && (
                  <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/8 text-white/40 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                {/* Payment summary */}
                <div
                  className="rounded-xl border overflow-hidden"
                  style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.bgBase }}
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: theme.colors.border }}>
                    <p className="text-white/35 text-xs uppercase tracking-wider">Resumen de pago</p>
                  </div>

                  {summary.loading ? (
                    <div className="px-4 py-6 flex items-center justify-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line text-sm animate-spin text-white/30" />
                      </div>
                      <p className="text-white/30 text-sm">Calculando…</p>
                    </div>
                  ) : summary.error ? (
                    <div className="px-4 py-4">
                      <p className="text-red-400 text-sm">{summary.error}</p>
                    </div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: theme.colors.border }}>
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-scissors-line text-xs text-white/40" />
                          </div>
                          <span className="text-white/55 text-sm">Precio del servicio</span>
                        </div>
                        <span className="text-white text-sm font-medium font-mono">
                          {formatCOP(summary.servicePrice)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-down-circle-line text-xs text-emerald-400/70" />
                          </div>
                          <span className="text-white/55 text-sm">Anticipo pagado</span>
                        </div>
                        <span className="text-emerald-400 text-sm font-medium font-mono">
                          − {formatCOP(summary.advanceAmount)}
                        </span>
                      </div>

                      <div
                        className="flex items-center justify-between px-4 py-3.5"
                        style={{ backgroundColor: 'rgba(16,185,129,0.06)' }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-money-dollar-circle-line text-xs text-emerald-400" />
                          </div>
                          <span className="text-white text-sm font-semibold">Restante a cobrar</span>
                        </div>
                        <span className="text-emerald-400 text-base font-bold font-mono">
                          {formatCOP(summary.remainingAmount)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment method selector */}
                {!summary.loading && !summary.error && (
                  <div>
                    <p className="text-white/35 text-xs uppercase tracking-wider mb-3">Método de cobro</p>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Cash */}
                      <button
                        onClick={() => setMethod('cash')}
                        disabled={completing}
                        className="flex flex-col items-center gap-2 px-4 py-4 rounded-xl border transition-all cursor-pointer disabled:opacity-50"
                        style={{
                          backgroundColor: method === 'cash'
                            ? 'rgba(16,185,129,0.10)'
                            : theme.colors.bgBase,
                          borderColor: method === 'cash'
                            ? 'rgba(16,185,129,0.35)'
                            : theme.colors.border,
                        }}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-full"
                          style={{
                            backgroundColor: method === 'cash'
                              ? 'rgba(16,185,129,0.15)'
                              : 'rgba(255,255,255,0.06)',
                          }}
                        >
                          <i className="ri-money-dollar-circle-line text-base"
                            style={{ color: method === 'cash' ? '#34d399' : 'rgba(255,255,255,0.35)' }}
                          />
                        </div>
                        <span className="text-sm font-medium"
                          style={{ color: method === 'cash' ? '#34d399' : 'rgba(255,255,255,0.45)' }}
                        >
                          Efectivo
                        </span>
                        {method === 'cash' && (
                          <div className="w-4 h-4 flex items-center justify-center rounded-full bg-emerald-500/20">
                            <i className="ri-check-line text-xs text-emerald-400" />
                          </div>
                        )}
                      </button>

                      {/* Transfer */}
                      <button
                        onClick={() => setMethod('transfer')}
                        disabled={completing}
                        className="flex flex-col items-center gap-2 px-4 py-4 rounded-xl border transition-all cursor-pointer disabled:opacity-50"
                        style={{
                          backgroundColor: method === 'transfer'
                            ? 'rgba(16,185,129,0.10)'
                            : theme.colors.bgBase,
                          borderColor: method === 'transfer'
                            ? 'rgba(16,185,129,0.35)'
                            : theme.colors.border,
                        }}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-full"
                          style={{
                            backgroundColor: method === 'transfer'
                              ? 'rgba(16,185,129,0.15)'
                              : 'rgba(255,255,255,0.06)',
                          }}
                        >
                          <i className="ri-exchange-dollar-line text-base"
                            style={{ color: method === 'transfer' ? '#34d399' : 'rgba(255,255,255,0.35)' }}
                          />
                        </div>
                        <span className="text-sm font-medium"
                          style={{ color: method === 'transfer' ? '#34d399' : 'rgba(255,255,255,0.45)' }}
                        >
                          Transferencia
                        </span>
                        {method === 'transfer' && (
                          <div className="w-4 h-4 flex items-center justify-center rounded-full bg-emerald-500/20">
                            <i className="ri-check-line text-xs text-emerald-400" />
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Info note */}
                {!summary.loading && !summary.error && (
                  <div className="flex items-start gap-2.5 rounded-xl px-4 py-3 border"
                    style={{
                      backgroundColor: 'rgba(16,185,129,0.05)',
                      borderColor: 'rgba(16,185,129,0.15)',
                    }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-information-line text-sm text-emerald-400/70" />
                    </div>
                    <p className="text-emerald-400/70 text-xs leading-relaxed">
                      Se registrará el pago restante como <strong className="text-emerald-400/90">ingreso confirmado</strong> y la cita quedará marcada como completada.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                className="flex items-center gap-3 px-6 py-4 border-t"
                style={{ borderColor: theme.colors.border }}
              >
                <button
                  onClick={onClose}
                  disabled={completing}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm text-white/50 hover:text-white border transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ borderColor: theme.colors.border, backgroundColor: 'transparent' }}
                >
                  Cancelar
                </button>

                <button
                  onClick={handleConfirm}
                  disabled={completing || summary.loading || !!summary.error}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{
                    backgroundColor: 'rgba(16,185,129,0.15)',
                    border: '1px solid rgba(16,185,129,0.30)',
                    color: '#34d399',
                  }}
                >
                  {completing ? (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line text-sm animate-spin" />
                      </div>
                      Guardando…
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-double-line text-sm" />
                      </div>
                      Confirmar cobro
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
